/**
 * Typed wrapper around the Kai-API attestation route
 * (`POST /attestations`, PLA-1108 / PLA-1110).
 *
 * Two kinds:
 *   - `ai_governance`: posted once per login after the user accepts the AI
 *     Governance Policy. `text` is the policy exactly as shown.
 *   - `goals`: posted when the user commits to the organization-set chief
 *     complaint goals from the reminder banner. `text` is the commitment
 *     statement as shown and `goals` the snapshot it covered.
 *
 * Identity (email, NPI) comes from the Bearer token server-side; the client
 * never sends it.
 */

import { apiPost } from './client';

export type AttestationKind = 'ai_governance' | 'goals';

/** One chief complaint goal as shown at commitment time. */
export interface GoalSnapshot {
  id: string;
  name: string;
  /** Target percent. */
  goal: number;
  /** Score in the active window, or null when there was no data. */
  score: number | null;
}

export type AttestationBody =
  | { kind: 'ai_governance'; text: string }
  | { kind: 'goals'; text: string; goals: GoalSnapshot[] };

export interface Attestation {
  attestationId: string;
  email: string;
  npi: string | null;
  kind: AttestationKind;
  createdAt: string;
}

export const submitAttestation = (body: AttestationBody, signal?: AbortSignal) =>
  apiPost<Attestation>('/attestations', body, { signal });
